import { useFeature } from "@growthbook/growthbook-react";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useQueryStates } from "nuqs";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  BarOptionDefinition,
  CountText,
  Loader,
  Notification,
  NotificationType,
  SlashCircleIcon,
  Toolbar,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";
import {
  PageHeader,
  PageLayout,
} from "@transfr-inc/dashboard-components/layouts";

import { BulkDeleteClassroomModal } from "@components/common/bulk-delete-classroom-modal";
import { RecordStatus } from "@components/custom/dropdowns/record-status-dropdown";
import { OrganizationFilter } from "@components/custom/forms";
import { ArchiveClassroomsModal } from "@components/modals";
import {
  invalidateClassroomsQueries,
  useUnarchiveClassrooms,
} from "@components/modals/archive-classrooms/api";
import { ShortcutAction } from "@components/shortcuts";
import {
  useArchiveClassroomEnabled,
  useArchiveClassroomVisible,
  useDeleteClassroomAllowed,
  useIntegrationOrgCodes,
} from "@hooks/queries/account-selectors";
import {
  trackClassroomsReactivated,
  trackClassroomStatusFilterSelected,
} from "@lib/event-trackers";
import { classroomsParams } from "@lib/utils/search-params-parsers";
import { Role } from "@models/role";
import { useCurrentUser, useFeatures, useRole } from "@queries";
import { useClassrooms } from "@queries/organization";
import { NewClassroomModal } from "@views/classrooms/creation/new-classroom-modal";

import { useMissionControlLiveCount } from "./use-live-count";
import {
  filterClassrooms,
  getArchiveresultMessage,
  getInstructorSortValue,
  getUnarchiveResultMessage,
  resolveProduct,
  resolveProductFilter,
} from "./utils";
import { ClassroomsFilters, ClassroomsTable } from "./views";

import "./index.scss";

export default function ClassroomList({ responsiveStyles = {}, className }) {
  const createClassroomButtonStyle = useFeature(
    "dashboard.create-classroom-button-style"
  );

  const [{ product: selectedProductId, status }, setClassroomsParams] =
    useQueryStates(classroomsParams);

  const currentUser = useCurrentUser();
  const role = useRole();
  const features = useFeatures();
  const queryClient = useQueryClient();

  const { organizationCode, userId } = currentUser || {};
  const { createClassroomEnabled, deleteClassroomEnabled } = features || {};

  const [selectedOrganization, setSelectedOrganization] = useState();

  const effectiveOrgCode = selectedOrganization?.code ?? organizationCode;

  const archiveClassroomEnabled = useArchiveClassroomEnabled(effectiveOrgCode);
  const archiveClassroomVisible = useArchiveClassroomVisible();
  const deleteClassroomAllowed = useDeleteClassroomAllowed(effectiveOrgCode);
  // Multi-org lists span child orgs; gate each row by its own org.
  const { isIntegrationOrgCode } = useIntegrationOrgCodes(effectiveOrgCode);
  const { data: classrooms, isFetching: isFetchingClassrooms } = useClassrooms(
    {
      userId,
      role,
      orgCode: effectiveOrgCode,
      status,
      isOrgSelected: !!selectedOrganization,
    },
    {
      enabled: !!(userId && role && effectiveOrgCode),
    }
  );
  const [filters, setFilters] = useState({
    classroomIds: [],
    products: [],
    organization: undefined,
    professionalLearning: false,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [stateNotification, setStateNotification] = useState();
  const [showDelete, setShowDelete] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showCreateClassroomModal, setShowCreateClassroomModal] = useState("");
  const [selectedClassrooms, setSelectedClassrooms] = useState([]);

  const selectedProduct = resolveProduct(selectedProductId);
  const missionControlLiveCount = useMissionControlLiveCount(
    selectedOrganization?.code
  );
  const pendingFilterEventRef = useRef(null);

  const onFiltersChange = (selectedFilters) => {
    setFilters((prev) => ({ ...prev, ...selectedFilters }));
  };

  useEffect(() => {
    setFilters({
      classroomIds: [],
      products: [],
      organization: selectedOrganization,
      professionalLearning: false,
    });
    setSearchTerm("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, organization: selectedOrganization }));
  }, [selectedOrganization]);

  const filteredClassrooms = useMemo(
    () =>
      filterClassrooms(classrooms ?? [], {
        ...filters,
        ...resolveProductFilter(selectedProductId),
      }),
    [classrooms, filters, selectedProductId]
  );

  const launchNotification = async (notification) => {
    setStateNotification(notification);
    setTimeout(() => {
      setStateNotification();
    }, 10000);
  };

  const onArchiveSuccess = ({ archived, blocked }) => {
    setSelectedClassrooms([]);
    invalidateClassroomsQueries(queryClient, archived);

    launchNotification({
      type: NotificationType.success,
      icon: ["fa-solid", "circle-check"],
      message: getArchiveresultMessage(archived, blocked),
    });
  };

  const { mutateAsync: unarchiveClassrooms, isPending: isUnarchiving } =
    useUnarchiveClassrooms({ invalidateOnSuccess: true });

  const onUnarchiveAction = async () => {
    const classroomIds = selectedClassrooms.map((c) => c.classroomId);
    try {
      await unarchiveClassrooms({ classroomIds, organizationCode: effectiveOrgCode });
      trackClassroomsReactivated(selectedClassrooms, {
        source: "list",
        batchId: crypto.randomUUID(),
      });
      setSelectedClassrooms([]);
      launchNotification({
        type: NotificationType.success,
        icon: ["fa-solid", "circle-check"],
        message: getUnarchiveResultMessage(classroomIds.length),
      });
    } catch (error) {
      launchNotification({
        type: NotificationType.error,
        icon: ["fa-solid", "circle-exclamation"],
        message:
          error?.data?.detail ?? "Something went wrong. Please try again.",
      });
    }
  };

  const onRowRemoved = () => {
    queryClient.invalidateQueries({
      queryKey: ["classrooms", userId, role, effectiveOrgCode, status],
    });
    setSelectedClassrooms([]);
  };

  const onClassroomAdded = (notification) => {
    launchNotification(notification);
    setShowCreateClassroomModal();
    if (notification) {
      queryClient.invalidateQueries({
        queryKey: ["classrooms", userId, role, effectiveOrgCode, status],
      });
    }
  };

  useEffect(() => {
    if (classrooms) {
      // Populate instructor sort key for classrooms
      classrooms.forEach((classroom) => {
        classroom.instructorSortKey = getInstructorSortValue(classroom);
      });
    }
  }, [classrooms]);

  useEffect(() => {
    if (!isFetchingClassrooms && pendingFilterEventRef.current) {
      const { newValue, previousValue } = pendingFilterEventRef.current;
      pendingFilterEventRef.current = null;
      trackClassroomStatusFilterSelected(
        newValue,
        previousValue,
        filteredClassrooms.length
      );
    }
  }, [isFetchingClassrooms, filteredClassrooms]);

  const bottomBarOptions = useMemo(() => {
    const hasData = selectedClassrooms?.length > 0;
    const allArchived =
      hasData && selectedClassrooms?.every((c) => c.isArchived);
    const allActive =
      hasData && selectedClassrooms?.every((c) => !c.isArchived);

    if (allArchived) {
      const options = [];
      if (archiveClassroomEnabled) {
        options.push(
          new BarOptionDefinition(onUnarchiveAction, {
            text: "Unarchive",
            icon: ["fa-light", "box-open"],
            primary: true,
            disabled: isUnarchiving,
          })
        );
      }
      if (deleteClassroomAllowed) {
        options.push(
          new BarOptionDefinition(() => setShowDelete(true), {
            text: "Delete",
            icon: ["fa-light", "fa-trash-can"],
            destructive: true,
          })
        );
      }
      return options;
    }

    if (allActive) {
      if (archiveClassroomEnabled) {
        return [
          new BarOptionDefinition(() => setShowArchive(true), {
            text: "Archive",
            icon: ["fa-regular", "box-archive"],
            primary: true,
          }),
        ];
      }
      if (deleteClassroomAllowed) {
        return [
          new BarOptionDefinition(() => setShowDelete(true), {
            text: "Delete",
            icon: ["fa-light", "fa-trash-can"],
            destructive: true,
          }),
        ];
      }
      return [];
    }
  }, [
    selectedClassrooms,
    isUnarchiving,
    archiveClassroomEnabled,
    deleteClassroomAllowed,
  ]);

  const selectionEnabled = useMemo(() => {
    if (!deleteClassroomEnabled) {
      return false;
    }

    if (status === RecordStatus.All) {
      return false;
    }

    return archiveClassroomEnabled || deleteClassroomAllowed;
  }, [
    deleteClassroomEnabled,
    archiveClassroomEnabled,
    deleteClassroomAllowed,
    status,
  ]);

  const orgFilter = (role === Role.MULTI_ORG_ADMIN.id ||
    role === Role.MULTI_ORG_VIEWER.id) && (
    <OrganizationFilter
      orgCode={organizationCode}
      onOrganizationSelected={setSelectedOrganization}
    />
  );

  const toolbar = (
    <Toolbar>
      {createClassroomEnabled && (
        <Button
          className={clsx(
            "create-classroom-button",
            createClassroomButtonStyle.value
          )}
          icon={["fa-regular", "plus"]}
          primary
          title="Create Classroom"
          tooltip="Create Classroom"
          onClick={() => setShowCreateClassroomModal(true)}
          size="small"
        />
      )}
    </Toolbar>
  );

  const pageHeader = (
    <PageHeader
      title={"Classrooms"}
      badge={
        isFetchingClassrooms ? "..." : <CountText count={classrooms?.length} />
      }
      middlePane={orgFilter}
    >
      {toolbar}
    </PageHeader>
  );

  return (
    <PageLayout
      header={pageHeader}
      responsiveStyles={responsiveStyles}
      className={clsx("classroom-list-page", className, "ce")}
    >
      {isFetchingClassrooms && <Loader fixed />}
      <ClassroomsFilters
        onFiltersChange={onFiltersChange}
        data={classrooms}
        filters={filters}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        selectedProduct={selectedProduct}
        onProductSelected={(product) =>
          setClassroomsParams({ product: product?.id ?? null })
        }
        organization={selectedOrganization}
        showStatusDropdown={archiveClassroomVisible}
        status={status}
        onStatusChange={(value) => {
          pendingFilterEventRef.current = {
            newValue: value,
            previousValue: status,
          };
          setClassroomsParams({ status: value, product: null });
        }}
      />
      {!isFetchingClassrooms && filteredClassrooms?.length === 0 && (
        <ShortcutAction className="no-data-message" disabled>
          <SlashCircleIcon></SlashCircleIcon>
          No data to display. Please try adjusting your search or filters.
        </ShortcutAction>
      )}
      {!isFetchingClassrooms && (
        <ClassroomsTable
          data={filteredClassrooms ?? []}
          responsiveStyles={responsiveStyles}
          selectionEnabled={selectionEnabled}
          bottomBarOptions={bottomBarOptions}
          missionControlLiveCount={missionControlLiveCount}
          onRowSelectionChange={setSelectedClassrooms}
          isIntegrationOrgCode={isIntegrationOrgCode}
        />
      )}
      {createClassroomEnabled && showCreateClassroomModal && (
        <NewClassroomModal
          show={showCreateClassroomModal}
          onClose={onClassroomAdded}
          orgCode={organizationCode}
          {...features}
        />
      )}
      <BulkDeleteClassroomModal
        onCancel={() => setShowDelete()}
        show={showDelete}
        deleteList={selectedClassrooms}
        onRowRemoved={onRowRemoved}
      />
      <ArchiveClassroomsModal
        open={showArchive}
        classrooms={selectedClassrooms}
        xapiConfig={{ source: "list" }}
        onCancel={() => setShowArchive(false)}
        onSuccess={onArchiveSuccess}
      />
      <div className="notification-container success-notification">
        {stateNotification && (
          <Notification
            type={stateNotification.type}
            icon={stateNotification.icon}
            onClose={() => {
              setStateNotification();
            }}
            closable
          >
            {stateNotification.message}
          </Notification>
        )}
      </div>
    </PageLayout>
  );
}
